import { Component, EventEmitter, Input, Output } from '@angular/core';

@Component({
    selector: 'app-class-profile-cards',
    templateUrl: './class-profile-cards.component.html',
    styleUrls: ['./class-profile-cards.component.scss'],
})
export class ClassProfileCardsComponent {
    @Input() title: string = ''; // Title of the card
    @Input() totalCount: number | null | undefined = 0; // Total count value
    @Input() progressBarPercentage: number | null | undefined = 0; // Progress bar value
    @Input() activeLabel: string = 'Active'; // Label for the active data
    @Input() activeCount: number | null | undefined = 0; // Count for the active data
    @Input() activeColor: string = '#9BE8FF'; // Color for the active indicator
    @Input() upcomingLabel: string = 'Upcoming'; // Label for the upcoming data
    @Input() upcomingCount: number | null | undefined = 0; // Count for the upcoming data
    @Input() upcomingColor: string = '#FDCD16'; // Color for the upcoming indicator
    @Input() remainingColor: string = '#e2e8f0'; // Color for the remaining/gray indicator
    @Input() selectedFilter: string | null = null; // Selected filter for highlighting
    @Input() signedOutLabel: string = 'Signed-out'; // Label for the signed-out data
    @Input() signedOutCount: number | null | undefined = 0; // Count for the signed-out data
    @Input() signedOutColor: string = '#FCCF7A'; // Color for the signed-out indicator
    @Input() notMarkedLabel: string = 'Not Marked'; // Label for the not marked data
    @Input() notMarkedCount: number | null | undefined = 0; // Count for the not marked data
    @Input() notMarkedColor: string = '#7ADCFC'; // Color for the not marked indicator
    @Input() showSignedOut: boolean = false;
    @Input() showNotMarked: boolean = false;
    @Output() filterClick = new EventEmitter<any>();

    private getClickedFilterCount(filterType: string): number {
        const normalizedType = String(filterType ?? '')
            .trim()
            .toLowerCase();
        const normalizedActive = String(this.activeLabel ?? '')
            .trim()
            .toLowerCase();
        const normalizedUpcoming = String(this.upcomingLabel ?? '')
            .trim()
            .toLowerCase();
        const normalizedSignedOut = String(this.signedOutLabel ?? '')
            .trim()
            .toLowerCase();
        const normalizedNotMarked = String(this.notMarkedLabel ?? '')
            .trim()
            .toLowerCase();

        if (normalizedType === normalizedActive) {
            return this.activeCount || 0;
        }
        if (normalizedType === normalizedUpcoming) {
            return this.upcomingCount || 0;
        }
        if (normalizedType === normalizedSignedOut) {
            return this.signedOutCount || 0;
        }
        if (normalizedType === normalizedNotMarked) {
            return this.notMarkedCount || 0;
        }

        return 0;
    }

    onFilterClick(filterName: string, filterType: string) {
        const count = this.getClickedFilterCount(filterType);
        const obj = { filterName, filterType, count };
        this.filterClick.emit(obj);
    }

    get activeProgressPercentage(): number {
        const total = this.totalCount || 0;
        return total > 0
            ? this.calculateProgressPercentage(this.activeCount || 0, total)
            : 0;
    }

    get upcomingProgressPercentage(): number {
        const total = this.totalCount || 0;
        return total > 0
            ? this.calculateProgressPercentage(this.upcomingCount || 0, total)
            : 0;
    }

    get signedOutProgressPercentage(): number {
        const total = this.totalCount || 0;
        return total > 0
            ? this.calculateProgressPercentage(this.signedOutCount || 0, total)
            : 0;
    }

    get notMarkedProgressPercentage(): number {
        const total = this.totalCount || 0;
        return total > 0
            ? this.calculateProgressPercentage(this.notMarkedCount || 0, total)
            : 0;
    }

    get remainingProgressPercentage(): number {
        const total = this.totalCount || 0;
        if (total === 0) {
            return 100; // Default to 100% gray when there are no students
        }
        const allocatedCount =
            (this.activeCount || 0) +
            (this.upcomingCount || 0) +
            (this.signedOutCount || 0) +
            (this.notMarkedCount || 0);
        const remainingCount = Math.max(0, total - allocatedCount);
        return this.calculateProgressPercentage(remainingCount, total);
    }

    private calculateProgressPercentage(part: number, total: number): number {
        return total ? (part / total) * 100 : 0;
    }
}
